/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";

/** Common Components */
import TaskCard from "../../board/TaskCard";
import RenderTabs from "../../ui/tabs/RenderTabs";
import NewTaskGrid from "../../tasks/NewTaskGrid";
import Board from "../../../components/board/TaskBoard";

/** Common Services & Data Files */
import { AppIcons } from "../../../data/appIcons";
import { groupByOptions } from "../../../pages/taskboard/Task.data";

/** RTK Query */
import { useLazyGetAllPriorityListQuery } from "../../../app/services/priorityAPI";
import { useLazyGetAllTaskTypeListQuery } from "../../../app/services/taskTypeAPI";
import { useGetAllProjectTeamListMutation } from "../../../app/services/projectTeamManagementAPI";
import { useGetProjectStatusWithPaginationMutation } from "../../../app/services/projectStatusAPI";
import { useUpdateBacklogTaskMutation } from "../../../app/services/backLogAPI";
import ToastService from "../../../services/toastService/ToastService";

/** CSS Files */
import "./Toolbar.scss";
import "../TaskLayout.scss";

const Toolbar = (props) => {

  const { projectId,
    taskListData,
    isDataLoading,
    isTeamPlanning,
    isShowActualTime,
    allowEditStatus,
    allowEditAssignee,
    onRemoveTask,
    onRefreshGrid,
    onLoadMoreTasks,
    isLoadingMore = false,
    hasMore = false,
    totalCount = null,
    isRefreshing = false,
    isError = false,
    onRetry,
    onClearFilters,
    hasActiveFilters = false,
    hasActiveSearch = false,
    customGridActions,
    onActionClick,
    onTaskPatch,
    selectionMode,
    onSelectionChange,
    listOnly = false,
    isShowRemainingTime = false,
  } = props;

  const [userList, setUserList] = useState([]);
  const [statusList, setStatusList] = useState([]);
  const [priorityList, setPriorityList] = useState([]);
  const [taskTypeList, setTaskTypeList] = useState([]);

  const [getUserList, {
    isLoading: isUserListLoading,
    isFetching: isUserNameFetching,
    isSuccess: isUserName,
    data: UserNameData,
  },] = useGetAllProjectTeamListMutation();

  const [getAllTaskStatus, {
    isLoading: isStatusListLoading,
    isFetching: isTaskStatusfetching,
    isSuccess: isTaskStatussuccess,
    data: isTaskStatusData,
  },] = useGetProjectStatusWithPaginationMutation();

  const [getAllPriorityList, {
    isLoading: isPriorityListLoading,
    isFetching: isPriorityFetching,
    isSuccess: isPrioritySuccess,
    data: isPriorityData,
  },] = useLazyGetAllPriorityListQuery();

  const [getAllTaskTypeList, {
    isFetching: isTaskTypeFetching,
    isSuccess: isTaskTypeFetched,
    data: TaskTypeData,
  },] = useLazyGetAllTaskTypeListQuery();

  const getUserListByProjectId = () => {
    const request = {
      pagination: {
        pageNumber: 1,
        pageSize: 100,
      },
      projectId: projectId ? projectId : 0,
    };
    getUserList(request);
  };

  const getTaskStatusList = () => {
    const request = {
      pagination: { pageNumber: 1, pageSize: 100, },
      filters: { searchText: "", },
      projectId: projectId
    };
    getAllTaskStatus(request);
  };

  useEffect(() => {
    if (projectId) {
      getTaskStatusList();
      getAllPriorityList();
      getAllTaskTypeList();
      getUserListByProjectId();
    }
  }, [projectId]);

  useEffect(() => {
    if (isUserName && !isUserNameFetching && UserNameData) {
      const userNameList = UserNameData.dataSource.filter((item) => item.isActive === true).map((item) => ({
        value: item.userId,
        label: item.userName,
      }));
      setUserList(userNameList);
    }
  }, [isUserName, isUserNameFetching, UserNameData]);

  useEffect(() => {
    if (!isTaskStatusfetching && isTaskStatussuccess && isTaskStatusData) {
      if (isTaskStatusData) {
        const status = isTaskStatusData.dataSource.map((item) => ({
          value: item.projectStatusConfigurationId,
          label: item.statusName,
          isActive: item.isActive,
          className: item.statusCategoryName
        }));
        setStatusList(status);
      }
    }
  }, [isTaskStatusfetching, isTaskStatussuccess, isTaskStatusData]);

  useEffect(() => {
    if (!isPriorityFetching && isPrioritySuccess && isPriorityData) {
      const priorityList = isPriorityData.map((item) => ({
        value: item.priorityId,
        label: item.priorityName,
        imagePath:
          item.priorityName === "High"
            ? AppIcons.High
            : item.priorityName === "Medium"
              ? AppIcons.Medium
              : item.priorityName === "Low"
                ? AppIcons.Low
                : null,
      }));

      setPriorityList(priorityList);
    }
  }, [isPriorityFetching, isPrioritySuccess, isPriorityData]);

  useEffect(() => {
    if (!isTaskTypeFetching && isTaskTypeFetched && TaskTypeData) {
      setTaskTypeList(TaskTypeData.map((item) => ({
        value: item.taskTypeId,
        label: item.taskTypeName,
      })));
    }
  }, [isTaskTypeFetching, isTaskTypeFetched, TaskTypeData]);

  const [updateBacklogTask] = useUpdateBacklogTaskMutation();

  const handleBoardItemMove = async (task, newStatusId) => {
    if (!allowEditStatus) return;

    const taskId = task?.taskId ?? task?.id;
    if (taskId == null || newStatusId == null) return;
    if (String(task.taskStatusId) === String(newStatusId)) return;

    const targetColumn = statusList.find((status) => String(status.value) === String(newStatusId));
    if (!targetColumn) return;

    const previous = {
      taskStatusId: task.taskStatusId,
      statusName: task.statusName,
    };

    if (onTaskPatch) {
      onTaskPatch(taskId, {
        taskStatusId: newStatusId,
        statusName: targetColumn.label,
      });
    }

    try {
      await updateBacklogTask({
        taskId,
        TaskStatusId: newStatusId,
      }).unwrap();

      if (!onTaskPatch) {
        onRefreshGrid?.();
      }
    } catch (error) {
      if (onTaskPatch) {
        onTaskPatch(taskId, previous);
      }
      ToastService.error("Could not update task");
    }
  };

  const listContent = (
    <>
      <NewTaskGrid
        isLoading={isDataLoading}
        isRefreshing={isRefreshing}
        isError={isError}
        onRetry={onRetry}
        onClearFilters={onClearFilters}
        hasActiveFilters={hasActiveFilters}
        hasActiveSearch={hasActiveSearch}
        userList={userList}
        projectId={projectId}
        statusList={statusList}
        priorityList={priorityList}
        taskTypeList={taskTypeList}
        taskListData={taskListData}
        onRemoveTask={onRemoveTask}
        onActionClick={onActionClick}
        onRefreshGrid={onRefreshGrid}
        isTeamPlanning={isTeamPlanning}
        isShowActualTime={isShowActualTime}
        isShowRemainingTime={isShowRemainingTime}
        allowEditStatus={allowEditStatus}
        allowEditAssignee={allowEditAssignee}
        customGridActions={customGridActions}
        totalCount={totalCount}
        selectionMode={selectionMode}
        onSelectionChange={onSelectionChange}
      />
      {taskListData?.length > 0 && hasMore && onLoadMoreTasks && (
        <div className="load-more-btn">
          <button
            type="button"
            className="btn btn-primary"
            onClick={onLoadMoreTasks}
            disabled={isLoadingMore}
            aria-busy={isLoadingMore}
          >
            {isLoadingMore ? (
              <>
                <span className="load-more-spinner" aria-hidden="true" />
                Loading tasks…
              </>
            ) : (
              "Load More Tasks"
            )}
          </button>
        </div>
      )}
    </>
  );

  const tabs = [
    {
      sMenuItemCaption: "List",
      icon: "lsicon:list-outline",
      component: listContent,
    },
    {
      sMenuItemCaption: "Board",
      icon: "fluent:board-split-28-regular",
      component: (
        <div className="mt-2 px-4 pt-4">
          <Board
            data={taskListData}
            columns={statusList}
            projectId={projectId}
            groupByField="statusName"
            groupByOptions={groupByOptions}
            renderItem={(item) => <TaskCard task={item} />}
            onItemMove={handleBoardItemMove}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="toolbar-section">
      {listOnly ? listContent : <RenderTabs tabs={tabs} />}
    </div>
  );
};

export default Toolbar;
